import { format } from 'date-fns'
import { CSSProperties, MouseEventHandler, ReactNode } from 'react'
import { LinkButton } from './Button'
import { ArrowSmallDownIcon } from '@heroicons/react/20/solid'
import { SortConfig } from 'hooks/useSortableData'
import { useDateFnsLocale } from 'hooks/useDateFnsLocale'

export const Table = ({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) => <table className={`m-0 min-w-full p-0 ${className}`}>{children}</table>

export const TrHead = ({
  children,
  className = '',
  style,
}: {
  children: ReactNode
  className?: string
  style?: object
}) => (
  <tr style={style} className={`bg-th-bkg-1 ${className}`}>
    {children}
  </tr>
)

export const Th = ({
  style,
  children,
  className = '',
  id,
  xBorder = false,
}: {
  style?: object
  children?: ReactNode
  className?: string
  id?: string
  xBorder?: boolean
}) => (
  <th
    style={style}
    className={`whitespace-nowrap px-2 py-3 text-xs font-normal text-th-fgd-3 first:pl-4 last:pr-4 ${
      xBorder ? 'border-x border-th-bkg-3' : ''
    } ${className}`}
    id={id}
    scope="col"
  >
    {children}
  </th>
)

interface TrBodyProps {
  children: ReactNode
  className?: string
  onClick?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseEnter?: (x: any) => void
  onMouseLeave?: MouseEventHandler
  style?: CSSProperties
}

export const TrBody = (props: TrBodyProps) => {
  const {
    children,
    className = '',
    onClick,
    onMouseEnter,
    onMouseLeave,
    style,
  } = props
  return (
    <tr
      className={className}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={style}
    >
      {children}
    </tr>
  )
}

TrBody.displayName = 'TrBody'

export const Td = ({
  children,
  className = '',
  xBorder = false,
}: {
  children: ReactNode
  className?: string
  xBorder?: boolean
}) => (
  <td
    className={`px-2 py-3 first:pl-4 last:pr-4 ${
      xBorder ? 'border-x border-th-bkg-3' : ''
    } ${className}`}
  >
    {children}
  </td>
)

export const TableDateDisplay = ({
  date,
  showSeconds,
}: {
  date: string | number
  showSeconds?: boolean
}) => {
  const dateFnsLocale = useDateFnsLocale()
  return (
    <>
      <p className="tracking-normal text-th-fgd-2">
        {format(
          new Date(date),
          dateFnsLocale.formatLong.date({ width: 'medium' }),
          { locale: dateFnsLocale },
        )}
      </p>
      <p className="text-xs text-th-fgd-4">
        {format(
          new Date(date),
          dateFnsLocale.formatLong.time({
            width: showSeconds ? 'medium' : 'short',
          }),
          {
            locale: dateFnsLocale,
          },
        )}
      </p>
    </>
  )
}

export const SortableColumnHeader = ({
  sort,
  sortConfig,
  sortKey,
  title,
  titleClass,
}: {
  sort: (key: string) => void
  sortConfig: SortConfig | null
  sortKey: string
  title: string
  titleClass?: string
}) => {
  return (
    <LinkButton
      className="flex items-center font-normal"
      onClick={() => sort(sortKey)}
    >
      <span className={`text-th-fgd-3 ${titleClass}`}>{title}</span>
      <ArrowSmallDownIcon
        className={`default-transition ml-1 size-4 shrink-0 ${
          sortConfig?.key === sortKey
            ? sortConfig?.direction === 'ascending'
              ? 'rotate-180'
              : 'rotate-0'
            : null
        }`}
      />
    </LinkButton>
  )
}
